import { Link } from "react-router-dom";
import { Target, Eye, Gem, HardHat, ShieldCheck, ClipboardCheck, MapPin, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SiteLayout, PageHero, SectionTag, DISTRICTS, useSEO } from "./shared";

const VALUES = [
  { icon: ShieldCheck, title: "Safety First", desc: "Every decision is made with the safety of occupants and property as the top priority." },
  { icon: ClipboardCheck, title: "Engineering Discipline", desc: "Systems are planned, installed and tested against relevant fire protection practice." },
  { icon: HardHat, title: "Accountability", desc: "We stand behind our installation and maintenance work with responsive service." },
];

export default function About() {
  useSEO(
    "About SecureFlame Fire Systems | Fire Protection Company in Tripura",
    "SecureFlame Fire Systems Private Limited — who we are, what we do, and our approach to fire protection engineering across Tripura."
  );
  return (
    <SiteLayout>
      <PageHero eyebrow="About SecureFlame" title="Engineering Fire Safety Across Tripura" subtitle="SecureFlame Fire Systems Private Limited plans, installs, tests and maintains complete fire protection systems for government, commercial, institutional, residential and industrial premises." />

      <section className="max-w-7xl mx-auto px-6 py-16 md:py-20 grid lg:grid-cols-2 gap-12 items-start">
        <div>
          <SectionTag>Who We Are</SectionTag>
          <p className="text-muted-foreground leading-relaxed">SecureFlame Fire Systems Private Limited is a fire protection engineering company based in Agartala, Tripura. We work with property owners, businesses, institutions and government establishments to design, supply, install, test, commission and maintain fire protection systems.</p>
        </div>
        <div>
          <SectionTag>What We Do</SectionTag>
          <p className="text-muted-foreground leading-relaxed">Our scope covers fire alarm systems, fire hydrant systems, automatic sprinkler systems, fire pump systems, fire extinguishers, emergency lighting and exit signage, CCTV, access control and public address systems — along with Annual Maintenance Contracts (AMC) and technical support toward Fire Safety Certificate / NOC applications.</p>
        </div>
      </section>

      <section className="bg-secondary text-white">
        <div className="max-w-7xl mx-auto px-6 py-16 md:py-20 grid md:grid-cols-3 gap-6">
          <div className="p-7 rounded-sm border border-white/10 bg-white/5">
            <Eye className="text-gold mb-4" size={28} />
            <h3 className="font-heading font-bold text-lg">Vision</h3>
            <p className="mt-2 text-sm text-slate-300">To be a dependable name in fire protection engineering across Tripura, known for sound installation and responsive service.</p>
          </div>
          <div className="p-7 rounded-sm border border-white/10 bg-white/5">
            <Target className="text-gold mb-4" size={28} />
            <h3 className="font-heading font-bold text-lg">Mission</h3>
            <p className="mt-2 text-sm text-slate-300">To deliver correctly engineered, properly installed and well-maintained fire protection systems for every client we serve.</p>
          </div>
          <div className="p-7 rounded-sm border border-white/10 bg-white/5">
            <Gem className="text-gold mb-4" size={28} />
            <h3 className="font-heading font-bold text-lg">Core Values</h3>
            <p className="mt-2 text-sm text-slate-300">Safety, engineering discipline, accountability and clear communication with every client and site team.</p>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-16 md:py-20">
        <div className="max-w-2xl mb-10">
          <SectionTag>Engineering &amp; Execution Capability</SectionTag>
          <h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight text-secondary">How we work.</h2>
        </div>
        <div className="grid sm:grid-cols-3 gap-6">
          {VALUES.map((v) => (
            <div key={v.title} className="p-6 rounded-sm border border-border bg-card">
              <v.icon className="text-primary mb-3" size={26} />
              <h3 className="font-heading font-bold text-sm text-secondary">{v.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{v.desc}</p>
            </div>
          ))}
        </div>
        <div className="mt-10 grid sm:grid-cols-2 gap-6">
          <div className="p-6 rounded-sm border border-border bg-muted">
            <h3 className="font-heading font-bold text-sm text-secondary uppercase tracking-wide">Quality Commitment</h3>
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">Materials, components and workmanship are selected and installed with attention to functional reliability, and systems are tested before handover.</p>
          </div>
          <div className="p-6 rounded-sm border border-border bg-muted">
            <h3 className="font-heading font-bold text-sm text-secondary uppercase tracking-wide">Safety Commitment</h3>
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">Field work follows safe practices on-site, and systems are handed over with clear guidance on operation and upkeep.</p>
          </div>
        </div>
      </section>

      <section className="bg-muted">
        <div className="max-w-7xl mx-auto px-6 py-16 md:py-20">
          <SectionTag>Service Coverage</SectionTag>
          <h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight text-secondary mb-8">Serving clients across Tripura.</h2>
          <div className="flex flex-wrap gap-3">
            {DISTRICTS.map((d) => (
              <span key={d} className="flex items-center gap-2 text-sm font-medium text-secondary bg-card border border-border px-4 py-2.5 rounded-sm"><MapPin size={14} className="text-primary" /> {d}</span>
            ))}
          </div>
        </div>
      </section>

      <section className="relative blueprint-bg text-white">
        <div className="relative max-w-4xl mx-auto px-6 py-16 md:py-20 text-center">
          <h2 className="font-heading font-black text-3xl sm:text-4xl tracking-tight">Want to Know More About SecureFlame?</h2>
          <div className="mt-8 flex flex-wrap gap-3.5 justify-center">
            <Link to="/contact?type=contact&subject=credentials"><Button size="lg" className="rounded-sm bg-primary hover:bg-primary/90 font-semibold" data-testid="about-credentials-btn">REQUEST CREDENTIALS</Button></Link>
            <Link to="/contact?type=site_visit"><Button size="lg" variant="outline" className="rounded-sm border-white/25 text-white bg-transparent hover:bg-white/10 gap-2 font-semibold" data-testid="about-sitevisit-btn">BOOK A SITE VISIT <ArrowRight size={16} /></Button></Link>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
